// Fix for the PDF.js worker file import
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  AppBar,
  Toolbar,
  Chip,
  Button,
  ButtonGroup,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme
} from '@mui/material';
import {
  ArrowBack,
  DownloadOutlined,
  WifiOff,
  Fullscreen,
  FullscreenExit,
  CloudDownload,
  CloudDone,
  ZoomIn,
  ZoomOut,
  Refresh,
  NavigateBefore,
  NavigateNext,
  PictureAsPdf,
  Description,
  ErrorOutline,
  OpenInNew
} from '@mui/icons-material';
import DocumentService from '../services/DocumentService';
import { useTheme as useAppTheme } from '../contexts/ThemeContext';
import * as mammoth from 'mammoth';

// Import React-PDF components and configure PDF.js worker
import { Document, Page, pdfjs } from 'react-pdf';

// Define the pinned version globally (set to 4.8.69)
const pinnedVersion = '4.8.69';

/**
 * Configure the PDF.js worker:
 * - Pin the CDN URL to the exact version (pinnedVersion) of your installed pdfjs-dist.
 * - Use the CDN worker when online and reachable.
 * - Otherwise, fall back to the locally installed worker.
 */
const setPdfWorker = async () => {
  const cdnWorkerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pinnedVersion}/build/pdf.worker.min.mjs`;

  // If offline, immediately fall back to the local worker.
  if (!navigator.onLine) {
    console.log('Offline mode detected, using local pdfjs worker');
    try {
      const localWorker = await import('pdfjs-dist/build/pdf.worker.min.mjs');
      pdfjs.GlobalWorkerOptions.workerSrc = localWorker.default || localWorker;
      console.log('Using local worker from mjs file (version ' + pinnedVersion + ').');
    } catch (err) {
      console.error('Failed to load local worker', err);
    }
    return;
  }

  try {
    // Try a HEAD request to check if the CDN worker is reachable.
    const response = await fetch(cdnWorkerSrc, { method: 'HEAD' });
    if (response.ok) {
      pdfjs.GlobalWorkerOptions.workerSrc = cdnWorkerSrc;
      console.log('Using CDN worker pinned to version:', pinnedVersion);
    } else {
      throw new Error('CDN worker not available (response not ok)');
    }
  } catch (error) {
    console.warn('CDN worker not reachable, falling back to local worker.', error);
    try {
      const localWorker = await import('pdfjs-dist/build/pdf.worker.min.mjs');
      pdfjs.GlobalWorkerOptions.workerSrc = localWorker.default || localWorker;
      console.log('Using local worker from mjs file (version ' + pinnedVersion + ').');
    } catch (err) {
      console.error('Failed to load local worker', err);
    }
  }
};

// Initialize the PDF.js worker immediately.
setPdfWorker();

const isMobile = () => /Mobi|Android/i.test(navigator.userAgent);

const DocumentViewer = ({ documentId, documentName, onBack }) => {
  const muiTheme = useTheme();
  const { darkMode } = useAppTheme();

  // State declarations
  const [content, setContent] = useState('');
  const [wordHtml, setWordHtml] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [documentType, setDocumentType] = useState('');
  const [originalBlob, setOriginalBlob] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState('Loading document...');
  const [isCached, setIsCached] = useState(false);
  const [cachingDocument, setCachingDocument] = useState(false);
  const [showPdfErrorDialog, setShowPdfErrorDialog] = useState(false);
  const [pdfObjectUrl, setPdfObjectUrl] = useState(null);
  const [pdfScale, setPdfScale] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [numPages, setNumPages] = useState(null);
  const [pageWidth, setPageWidth] = useState(0);
  const [reactPdfFailed, setReactPdfFailed] = useState(false);

  // Add refs for storing raw PDF data and load attempt counter
  const pdfContainerRef = useRef(null);
  const pdfDataRef = useRef(null);
  const loadAttemptRef = useRef(0);

  const isContentTypePdf = (contentType, fileName) => {
    if (contentType && (
      contentType.includes('pdf') ||
      contentType === 'application/pdf' ||
      contentType === 'application/x-pdf'
    )) {
      return true;
    }
    if (fileName && fileName.toLowerCase().endsWith('.pdf')) {
      return true;
    }
    return false;
  };

  const isContentTypeWord = (contentType, fileName) => {
    if (contentType && (
      contentType.includes('word') ||
      contentType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      contentType === 'application/msword'
    )) {
      return true;
    }
    if (fileName && (fileName.toLowerCase().endsWith('.docx') || fileName.toLowerCase().endsWith('.doc'))) {
      return true;
    }
    return false;
  };

  const determineDocumentType = (filename, mimeType) => {
    if (!filename && !mimeType) return 'other';
    if (filename && (filename.toLowerCase().endsWith('.docx') || filename.toLowerCase().endsWith('.doc'))) {
      return 'word';
    }
    if (isContentTypePdf(mimeType, filename)) return 'pdf';
    if (isContentTypeWord(mimeType, filename)) return 'word';
    const extension = filename ? filename.split('.').pop().toLowerCase() : '';
    const typeMap = {
      'xls': 'excel',
      'xlsx': 'excel',
      'ppt': 'powerpoint',
      'pptx': 'powerpoint',
      'txt': 'text',
      'md': 'text'
    };
    if (typeMap[extension]) return typeMap[extension];
    if (mimeType) {
      if (mimeType.includes('excel')) return 'excel';
      if (mimeType.includes('powerpoint')) return 'powerpoint';
      if (mimeType.includes('text/plain')) return 'text';
      if (mimeType.includes('image/')) return 'image';
    }
    return 'other';
  };

  // Modified to store raw PDF data for offline use.
  const handleDocumentBlob = async (blob, fileName) => {
    console.group('PDF Blob Processing Diagnostics');
    console.log('Blob Details:', {
      size: blob.size,
      type: blob.type,
      name: fileName,
      instanceof: blob instanceof Blob,
    });
  
    try {
      if (!(blob instanceof Blob)) {
        throw new Error('Invalid blob type: not a Blob instance');
      }
      
      // Store the raw PDF data in our ref for offline use
      const arrayBuffer = await blob.arrayBuffer();
      pdfDataRef.current = new Uint8Array(arrayBuffer);
      
      const objectUrl = URL.createObjectURL(blob);
      console.log('Object URL created successfully', objectUrl);
      setOriginalBlob(blob);
      setPdfObjectUrl(objectUrl);
      setContent(objectUrl);
      setPdfScale(1);
      setCurrentPage(1);
      console.log('PDF processing completed successfully');
      console.groupEnd();
    } catch (error) {
      console.error('Comprehensive PDF Processing Error:', error);
      setShowPdfErrorDialog(true);
      console.groupEnd();
    }
  };

  const handleWordDocument = async (blob, fileName) => {
    try {
      setLoadingMessage('Converting Word document...');
      console.log('Processing Word document:', fileName);
      const result = await mammoth.convertToHtml({ arrayBuffer: await blob.arrayBuffer() });
      setWordHtml(result.value);
      setOriginalBlob(blob);
      console.log('Word document converted successfully');
      return true;
    } catch (error) {
      console.error('Error processing Word document:', error);
      setError(`Failed to convert Word document: ${error.message}`);
      return false;
    }
  };

  const enhancedFetch = async (url) => {
    console.log('Attempting enhanced fetch for document:', url);
    const attempts = [
      async () => {
        console.log('Attempt 1: Standard fetch');
        const response = await fetch(url, {
          method: 'GET',
          cache: 'no-store',
          headers: { 'Accept': '*/*' },
          credentials: 'same-origin',
          mode: 'cors',
        });
        if (!response.ok) {
          throw new Error(`Server responded with ${response.status}: ${response.statusText}`);
        }
        return response.blob();
      },
      async () => {
        console.log('Attempt 2: Using XMLHttpRequest');
        return new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('GET', url, true);
          xhr.responseType = 'blob';
          xhr.onload = function() {
            if (this.status >= 200 && this.status < 300) {
              resolve(this.response);
            } else {
              reject(new Error(`XHR request failed: ${this.status}`));
            }
          };
          xhr.onerror = () => reject(new Error('XHR network request failed'));
          xhr.send();
        });
      },
      async () => {
        console.log('Attempt 3: Fetch with no-cors mode');
        const response = await fetch(url, {
          method: 'GET',
          cache: 'no-store',
          mode: 'no-cors',
        });
        return response.blob();
      }
    ];
    for (let i = 0; i < attempts.length; i++) {
      try {
        const blob = await attempts[i]();
        console.log(`Fetch attempt ${i+1} succeeded, got blob size:`, blob.size);
        return blob;
      } catch (error) {
        console.warn(`Fetch attempt ${i+1} failed:`, error);
      }
    }
    throw new Error('All fetch attempts failed to retrieve the document');
  };

  // Updated loadDocumentSafely with retry logic and offline handling.
  const loadDocumentSafely = async () => {
    if (!documentId || !documentName) {
      setError('Invalid document information');
      setLoading(false);
      return;
    }
    
    // Increment the load attempt counter
    loadAttemptRef.current += 1;
    const currentAttempt = loadAttemptRef.current;
    
    try {
      setLoading(true);
      setError(null);
      setLoadingMessage('Loading document...');
      const cached = await DocumentService.isDocumentCached(documentId);
      setIsCached(cached);
      if (cached) {
        try {
          const cachedContent = await DocumentService.getDocumentContent(documentId);
          const docType = determineDocumentType(documentName, cachedContent.type);
          setDocumentType(docType);
          if (docType === 'pdf') {
            await handleDocumentBlob(cachedContent, documentName);
          } else if (docType === 'word') {
            await handleWordDocument(cachedContent, documentName);
          } else if (docType === 'image') {
            const imageUrl = URL.createObjectURL(cachedContent);
            setContent(imageUrl);
            setOriginalBlob(cachedContent);
          } else {
            setOriginalBlob(cachedContent);
            if (docType === 'text') {
              const text = await cachedContent.text();
              setContent(text);
            } else {
              setContent(URL.createObjectURL(cachedContent));
            }
          }
          setLoading(false);
          return;
        } catch (cacheError) {
          console.error('Error loading from cache:', cacheError);
          // If this is our first attempt, try again once more
          if (currentAttempt === 1) {
            console.log('Retrying document load after cache error');
            loadDocumentSafely();
            return;
          }
        }
      }
      if (!navigator.onLine) {
        throw new Error('Document is not available offline. Please connect to the internet to download it.');
      }
      const documentMetadata = await DocumentService.getDocumentMetadata(documentId);
      if (!documentMetadata || !documentMetadata.path) {
        throw new Error('Could not retrieve document location');
      }
      setLoadingMessage('Downloading document...');
      const blob = await enhancedFetch(documentMetadata.url || documentMetadata.path);
      const docType = determineDocumentType(documentName, blob.type);
      setDocumentType(docType);
      if (docType === 'pdf') {
        await handleDocumentBlob(blob, documentName);
      } else if (docType === 'word') {
        await handleWordDocument(blob, documentName);
      } else if (docType === 'image') {
        const imageUrl = URL.createObjectURL(blob);
        setContent(imageUrl);
        setOriginalBlob(blob);
      } else {
        setOriginalBlob(blob);
        if (docType === 'text') {
          const text = await blob.text();
          setContent(text);
        } else {
          setContent(URL.createObjectURL(blob));
        }
      }
      await DocumentService.storeBlobInIndexedDB(documentId, blob);
      await DocumentService.updateDocumentCacheStatus(documentId);
      setIsCached(true);
    } catch (error) {
      console.error('Error loading document:', error);
      setError(error.message || 'Failed to load document');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!originalBlob) {
      console.error('No blob available for download');
      return;
    }
    try {
      const url = URL.createObjectURL(originalBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = documentName || 'document';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
      }, 10000);
      console.log('Document download initiated successfully');
    } catch (error) {
      console.error('Error in direct download:', error);
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(originalBlob, documentName || 'document');
      } else {
        alert('Download failed. Please try again or use a different browser.');
      }
    }
  };

  const handleCacheDocument = async () => {
    if (cachingDocument || isCached || !documentId) return;
    setCachingDocument(true);
    try {
      await DocumentService.cacheDocumentForOffline(documentId);
      setIsCached(true);
    } catch (error) {
      console.error('Error caching document:', error);
    } finally {
      setCachingDocument(false);
    }
  };

  const handleOpenPdfExternally = () => {
    if (!originalBlob && !pdfObjectUrl) {
      console.error('No PDF blob or URL available to open');
      setShowPdfErrorDialog(false);
      return;
    }
    try {
      const url = pdfObjectUrl || URL.createObjectURL(originalBlob);
      window.open(url, '_blank');
      setShowPdfErrorDialog(false);
    } catch (error) {
      console.error('Error opening PDF externally:', error);
      alert('Could not open PDF. Please try downloading the file instead.');
    }
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setTotalPages(numPages);
    setLoading(false);
  };

  const handleZoomIn = () => setPdfScale(prev => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => setPdfScale(prev => Math.max(prev - 0.25, 0.5));
  const handleZoomReset = () => setPdfScale(1);
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };
  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  useEffect(() => {
    if (pdfContainerRef.current && documentType === 'pdf') {
      const updateWidth = () => {
        const container = pdfContainerRef.current;
        if (container) {
          setPageWidth(container.clientWidth * 0.9);
        }
      };
      updateWidth();
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }
  }, [documentType, pdfContainerRef]);

  // Updated useEffect: retry loadDocumentSafely when coming online after errors.
  useEffect(() => {
    loadDocumentSafely();
  
    const handleOnline = () => {
      console.log('Device went online');
      setIsOffline(false);
      if (error) {
        loadDocumentSafely();
      }
    };
  
    const handleOffline = () => {
      console.log('Device went offline');
      setIsOffline(true);
    };
  
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
  
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [documentId, documentName]);

  // Cleanup: also clear the in-memory PDF data reference.
  useEffect(() => {
    return () => {
      if (content && (documentType === 'pdf' || documentType === 'other' || documentType === 'image')) {
        if (content.startsWith('blob:')) {
          URL.revokeObjectURL(content);
        }
      }
      if (pdfObjectUrl) {
        URL.revokeObjectURL(pdfObjectUrl);
      }
      pdfDataRef.current = null;
    };
  }, [content, documentType, pdfObjectUrl]);

  // Updated fallback viewer to work with raw PDF data when offline.
  const renderFallbackPdfViewer = () => {
    if (!content && !pdfDataRef.current) return null;
    
    let displayUrl = content;
    if (!displayUrl && pdfDataRef.current) {
      try {
        const freshBlob = new Blob([pdfDataRef.current], { type: 'application/pdf' });
        displayUrl = URL.createObjectURL(freshBlob);
      } catch (error) {
        console.error('Error creating fallback PDF blob:', error);
      }
    }
    
    if (!displayUrl) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="error" variant="h6">
            Failed to render PDF
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleDownload} 
            sx={{ mt: 2 }}
          >
            Download File
          </Button>
        </Box>
      );
    }
    
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{
          p: 1,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Chip
              icon={<ErrorOutline fontSize="small" />}
              label="Using basic PDF viewer"
              color="warning"
              size="small"
              sx={{ mr: 2 }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<OpenInNew />}
              onClick={handleOpenPdfExternally}
            >
              Open in New Tab
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={<DownloadOutlined />}
              onClick={handleDownload}
            >
              Download
            </Button>
          </Box>
        </Box>
        <Box sx={{ flexGrow: 1, overflow: 'auto', position: 'relative' }}>
          <iframe
            src={displayUrl}
            style={{ width: '100%', height: '100%', border: 'none' }}
            title="PDF Document"
          />
        </Box>
      </Box>
    );
  };

  // Updated renderPdfViewer to use in-memory data when offline.
  const renderPdfViewer = () => {
    if (!content && !pdfDataRef.current) return null;
  
    if (reactPdfFailed) {
      return renderFallbackPdfViewer();
    }
  
    // Prefer the in-memory PDF data over the blob URL when offline.
    const fileSource = isOffline && pdfDataRef.current 
      ? { data: pdfDataRef.current }
      : content;
  
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{
          p: 1,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ButtonGroup size="small" variant="outlined" aria-label="page navigation">
              <Tooltip title="Previous Page">
                <span>
                  <IconButton size="small" onClick={handlePrevPage} disabled={currentPage <= 1}>
                    <NavigateBefore />
                  </IconButton>
                </span>
              </Tooltip>
              <Box sx={{ px: 2, display: 'flex', alignItems: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 0 }}>
                <Typography variant="body2">
                  Page {currentPage} of {totalPages}
                </Typography>
              </Box>
              <Tooltip title="Next Page">
                <span>
                  <IconButton size="small" onClick={handleNextPage} disabled={currentPage >= totalPages}>
                    <NavigateNext />
                  </IconButton>
                </span>
              </Tooltip>
            </ButtonGroup>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Zoom Out">
              <IconButton size="small" onClick={handleZoomOut} disabled={pdfScale <= 0.5}>
                <ZoomOut fontSize="small" />
              </IconButton>
            </Tooltip>
            <Typography variant="body2" sx={{ minWidth: 50, textAlign: 'center' }}>
              {Math.round(pdfScale * 100)}%
            </Typography>
            <Tooltip title="Zoom In">
              <IconButton size="small" onClick={handleZoomIn} disabled={pdfScale >= 3}>
                <ZoomIn fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Reset Zoom">
              <IconButton size="small" onClick={handleZoomReset}>
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Box
          ref={pdfContainerRef}
          sx={{
            flexGrow: 1,
            overflow: 'auto',
            display: 'flex',
            justifyContent: 'center',
            py: 2
          }}
        >
          {(() => {
            try {
              return (
                <Document
                  file={fileSource}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={(error) => {
                    console.error('Error loading PDF with React PDF:', error);
                    setReactPdfFailed(true);
                    console.log('Switching to fallback PDF viewer');
                  }}
                  loading={
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4 }}>
                      <CircularProgress size={30} sx={{ mb: 2 }} />
                      <Typography>Loading PDF...</Typography>
                    </Box>
                  }
                  noData={
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                      <Typography>No PDF document loaded.</Typography>
                    </Box>
                  }
                  error={
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                      <Typography color="error">Failed to load PDF document.</Typography>
                      <Button variant="outlined" onClick={handleOpenPdfExternally} sx={{ mt: 2, mr: 1 }}>
                        Open in New Tab
                      </Button>
                      <Button variant="contained" color="primary" onClick={handleDownload} sx={{ mt: 2 }}>
                        Download PDF
                      </Button>
                    </Box>
                  }
                  options={{
                    cMapUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pinnedVersion}/cmaps/`,
                    cMapPacked: true,
                    standardFontDataUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pinnedVersion}/standard_fonts/`,
                    disableStream: isOffline,
                    disableAutoFetch: isOffline
                  }}
                >
                  <Page
                    pageNumber={currentPage}
                    scale={pdfScale}
                    width={pageWidth}
                    renderAnnotationLayer={!isOffline}
                    renderTextLayer={!isOffline}
                    renderInteractiveForms={!isOffline}
                    error={
                      <Typography color="error" sx={{ p: 2 }}>
                        Error rendering this page. Try changing pages or reloading.
                      </Typography>
                    }
                  />
                </Document>
              );
            } catch (err) {
              console.error('Critical error in PDF rendering:', err);
              return (
                <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Typography color="error" sx={{ mb: 2 }}>
                    Error with PDF viewer component. Falling back to basic viewer.
                  </Typography>
                  <Box sx={{ width: '100%', height: '100%', flex: 1 }}>
                    <iframe
                      src={fileSource.data ? URL.createObjectURL(new Blob([fileSource.data], { type: 'application/pdf' })) : fileSource}
                      style={{ width: '100%', height: '100%', border: 'none' }}
                      title="PDF Document"
                    />
                  </Box>
                </Box>
              );
            }
          })()}
        </Box>
      </Box>
    );
  };

  const renderDocument = () => {
    if (!content && !wordHtml) return null;
    switch (documentType) {
      case 'pdf':
        return renderPdfViewer();
      case 'word':
        return (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 3, overflow: 'auto' }}>
            <Box sx={{
              p: 1,
              mb: 2,
              bgcolor: 'background.paper',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1
            }}>
              <Typography variant="body1" fontWeight="medium">
                <Description sx={{ verticalAlign: 'middle', mr: 1 }} />
                Word Document Viewer
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Tooltip title="Zoom Out">
                  <IconButton size="small" onClick={handleZoomOut} disabled={pdfScale <= 0.5}>
                    <ZoomOut fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Typography variant="body2" sx={{ minWidth: 50, textAlign: 'center' }}>
                  {Math.round(pdfScale * 100)}%
                </Typography>
                <Tooltip title="Zoom In">
                  <IconButton size="small" onClick={handleZoomIn} disabled={pdfScale >= 3}>
                    <ZoomIn fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reset Zoom">
                  <IconButton size="small" onClick={handleZoomReset}>
                    <Refresh fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Box sx={{
              bgcolor: 'background.paper',
              p: 3,
              borderRadius: 1,
              boxShadow: 1,
              overflow: 'auto',
              flex: 1,
              transform: `scale(${pdfScale})`,
              transformOrigin: 'top center',
              transition: 'transform 0.2s ease',
              '& img': { maxWidth: '100%' }
            }}>
              <div dangerouslySetInnerHTML={{ __html: wordHtml }} />
            </Box>
          </Box>
        );
      case 'excel':
      case 'powerpoint':
        return (
          <Box sx={{ p: 3, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
            <Typography variant="h6" gutterBottom>
              {documentType.charAt(0).toUpperCase() + documentType.slice(1)} Document
            </Typography>
            <Typography variant="body1" paragraph color="text.secondary">
              This document cannot be previewed directly in the browser.
            </Typography>
            <Button variant="contained" startIcon={<DownloadOutlined />} onClick={handleDownload} sx={{ mt: 2 }}>
              Download File
            </Button>
          </Box>
        );
      case 'image':
        return (
          <Box sx={{ p: 3, textAlign: 'center', height: '100%', overflow: 'auto' }}>
            <img
              src={content}
              alt={documentName}
              style={{ maxWidth: '100%', height: 'auto', transition: 'transform 0.2s ease', transform: `scale(${pdfScale})`, transformOrigin: 'top center' }}
            />
          </Box>
        );
      case 'text':
        return (
          <Box sx={{ p: 3, whiteSpace: 'pre-wrap', fontFamily: 'monospace', overflow: 'auto', height: '100%' }}>
            {content}
          </Box>
        );
      default:
        return (
          <Box sx={{ p: 3, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
            <Typography variant="h6" gutterBottom>
              This file type cannot be previewed directly
            </Typography>
            <Typography variant="body1" paragraph color="text.secondary">
              {documentName}
            </Typography>
            <Button variant="contained" startIcon={<DownloadOutlined />} onClick={handleDownload} sx={{ mt: 2 }}>
              Download File
            </Button>
          </Box>
        );
    }
  };

  const handleBackClick = () => {
    if (onBack) onBack();
  };

  return (
    <Box sx={{ flexGrow: 1, height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: isFullscreen ? (darkMode ? 'background.default' : '#f5f5f5') : 'background.default' }}>
      <AppBar position="static">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={handleBackClick} aria-label="back">
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, ml: 2 }}>
            {documentName ? documentName.replace(/\.[^/.]+$/, "") : 'Document Viewer'}
          </Typography>
          {isOffline && (
            <Chip icon={<WifiOff />} label="Offline" color="warning" size="small" sx={{ mr: 2 }} />
          )}
          {documentType === 'pdf' && (
            <Chip icon={<PictureAsPdf />} label="PDF" color="default" size="small" sx={{ mr: 1 }} />
          )}
          {documentType === 'word' && (
            <Chip icon={<Description />} label="Word" color="primary" size="small" sx={{ mr: 1 }} />
          )}
          {isCached ? (
            <Chip icon={<CloudDone />} label="Available Offline" color="success" size="small" sx={{ mr: 1 }} />
          ) : (
            <Button color="inherit" onClick={handleCacheDocument} disabled={cachingDocument || isOffline} startIcon={cachingDocument ? <CircularProgress size={16} color="inherit" /> : <CloudDownload />} size="small" sx={{ mr: 1 }}>
              {cachingDocument ? 'Caching...' : 'Save Offline'}
            </Button>
          )}
          <IconButton color="inherit" onClick={() => setIsFullscreen(!isFullscreen)} sx={{ mr: 1 }} aria-label={isFullscreen ? 'exit fullscreen' : 'fullscreen'}>
            {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
          </IconButton>
          <IconButton color="inherit" onClick={handleDownload} disabled={!originalBlob} aria-label="download">
            <DownloadOutlined />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Paper elevation={isFullscreen ? 0 : 3} sx={{
          flexGrow: 1,
          m: isFullscreen ? 0 : 2,
          p: 0,
          overflow: 'auto',
          backgroundColor: darkMode ? 'background.paper' : '#fff',
          height: isFullscreen ? 'calc(100vh - 64px)' : 'auto',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column'
        }}>
        {loading && documentType !== 'pdf' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography variant="body1">{loadingMessage}</Typography>
          </Box>
        )}
        {error && (
          <Box sx={{ p: 3 }}>
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            <Button variant="outlined" startIcon={<ArrowBack />} onClick={handleBackClick}>
              Go Back
            </Button>
          </Box>
        )}
        {!error && renderDocument()}
      </Paper>
      <Dialog open={showPdfErrorDialog} onClose={() => setShowPdfErrorDialog(false)}>
        <DialogTitle>
          <ErrorOutline color="warning" sx={{ mr: 1, verticalAlign: 'middle' }} />
          PDF Viewer Issue
        </DialogTitle>
        <DialogContent>
          <Typography paragraph>
            There was a problem displaying the PDF document. This could be due to:
          </Typography>
          <ul>
            <li>Mobile browser limitations</li>
            <li>PDF compatibility issues</li>
            <li>Document protection settings</li>
          </ul>
          <Typography paragraph>
            You can try using our basic viewer, open the PDF in a dedicated viewer app, or download it instead.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setReactPdfFailed(true);
              setShowPdfErrorDialog(false);
            }}
            startIcon={<Description />}
          >
            Use Basic Viewer
          </Button>
          <Button
            onClick={handleOpenPdfExternally}
            startIcon={<OpenInNew />}
          >
            Open in New Tab
          </Button>
          <Button
            onClick={handleDownload}
            startIcon={<DownloadOutlined />}
            variant="contained"
            color="primary"
          >
            Download PDF
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentViewer;
